/**
 * poolDispatch · pure helpers behind the live Lead Pool board (sales-lead-pool-dispatch §4).
 *
 * The components render, this decides: which view a URL lands on, how a queue maps to query
 * params, what colour a waiting timer is, what the brief chips say, how the server's dispatch
 * shapes are normalised. No React, no fetch — tested in `poolDispatch.test.ts`.
 */
import { splitNotes } from './engagementBrief'
import type {
  AutoDistributeResult,
  ClaimNextResult,
  DispatchPolicyApi,
  PoolPulse,
  DispatchWaitingItem,
  LeaderboardEntry,
  PoolLeadItem,
  PoolQueueKey,
  PoolTemperature,
  RepLoad,
  SettableCustomerType,
} from './types'

// ─── Views and URL state ───────────────────────────────────────────────────────────

export type PoolView = 'live' | 'backlog' | 'team' | 'lost'

/** The URL key that picks the view. */
export const VIEW_PARAM = 'view'
/** The URL key for the live board's queue chip. */
export const QUEUE_PARAM = 'queue'

/**
 * The register's own filter keys. A link carrying any of them — the Pipeline / My Day banner's
 * `?status=PROVISIONAL&owner=me`, a shared filtered view — predates the live board and must still
 * land in the table with those filters applied.
 */
export const BACKLOG_PARAMS = [
  'status',
  'owner',
  'source',
  'uploadedBy',
  'engaged',
  'hasDoneBusiness',
  'search',
  'page',
  'sort',
] as const

/**
 * Which view a URL shows. `team` needs a manager; `lost` needs a lost-case leader (a manager or the
 * configured lost-case owner — sales-lost-case-handover LC6). Anyone else asking for either gets
 * the live board.
 */
export function resolvePoolView(params: URLSearchParams, isManager: boolean, isLostLeader = false): PoolView {
  const raw = (params.get(VIEW_PARAM) ?? '').toLowerCase()
  if (raw === 'team') return isManager ? 'team' : 'live'
  if (raw === 'lost') return isLostLeader ? 'lost' : 'live'
  if (raw === 'backlog') return 'backlog'
  if (raw === 'live') return 'live'
  return BACKLOG_PARAMS.some((k) => params.has(k)) ? 'backlog' : 'live'
}

/**
 * The URL updates that switch to `view`. The live board is the default, so it drops `view` —
 * unless backlog filters are still in the URL, which would otherwise pull the page straight back
 * into the table. Backlog filters are kept either way, so returning to the table restores them.
 */
export function viewParamUpdate(view: PoolView, params: URLSearchParams): Record<string, string | null> {
  if (view === 'live') {
    const hasBacklogFilters = BACKLOG_PARAMS.some((k) => params.has(k))
    return { [VIEW_PARAM]: hasBacklogFilters ? 'live' : null }
  }
  return { [VIEW_PARAM]: view }
}

/**
 * The lead drawer's open lead, held in LOCAL state and synced from the URL.
 *
 * `url` = the last `?selected=` value actually seen; `open` = the lead the drawer shows. Open and
 * close change only `open` (synchronously) and then write the URL, which React Router applies
 * later inside a transition. Until then the URL still equals `url`, so nothing changes; when a URL
 * change does land (our write, back/forward, a deep link) `url` moves and `open` follows it.
 */
export interface DrawerSync {
  url: string | null
  open: string | null
}

export function syncDrawerFromUrl(state: DrawerSync, urlSelected: string | null): DrawerSync {
  return urlSelected === state.url ? state : { url: urlSelected, open: urlSelected }
}

const QUEUE_KEYS: readonly PoolQueueKey[] = ['WEBSITE', 'EMAIL', 'RETURNING', 'IMPORTED', 'REFERRAL', 'ALL']

/** The `queue` URL value as a contract key; anything else (or nothing) is ALL. */
export function parseQueue(raw: string | null | undefined): PoolQueueKey {
  const v = (raw ?? '').toUpperCase()
  return (QUEUE_KEYS as readonly string[]).includes(v) ? (v as PoolQueueKey) : 'ALL'
}

/** URL update for a chip: ALL clears the param rather than writing `queue=ALL`. */
export function queueParamUpdate(queue: string): Record<string, string | null> {
  const parsed = parseQueue(queue)
  return { [QUEUE_PARAM]: parsed === 'ALL' ? null : parsed }
}

/**
 * The server's urgency order for HOT rows (overdue oldest-first, then newest), so overdue leads are
 * on the first page. One constant: the param value is pending confirmation from the backend. The
 * client sorts too (`sortHotForClaim`) as a safety net.
 */
export const HOT_URGENCY_SORT = 'urgency'

/** Query params for the live board's lists: one temperature, the chosen queue, unowned only. */
export function liveListParams(
  temperature: PoolTemperature,
  queue: PoolQueueKey,
  size: number,
): { temperature: PoolTemperature; queue?: PoolQueueKey; sort?: string; ownerId: 'unowned'; page: 1; size: number } {
  return {
    temperature,
    ...(queue !== 'ALL' ? { queue } : {}),
    // WARM keeps the server's default order (waitingSince desc).
    ...(temperature === 'HOT' ? { sort: HOT_URGENCY_SORT } : {}),
    // The live board is for claiming: an owned provisional record is not on offer here.
    ownerId: 'unowned',
    page: 1,
    size,
  }
}

/** Claim-next body: the queue only when one is chosen. */
export function claimNextBody(queue: PoolQueueKey): { queue?: PoolQueueKey } {
  return queue === 'ALL' ? {} : { queue }
}

// ─── Temperature ───────────────────────────────────────────────────────────────────

/** The row's temperature, tolerant of case and of an older server that sends none (COLD). */
export function temperatureOf(row: Pick<PoolLeadItem, 'temperature'>): PoolTemperature {
  const v = (row.temperature ?? '').toString().toUpperCase()
  return v === 'HOT' || v === 'WARM' ? v : 'COLD'
}

/** Badge classes per temperature — HOT warm-orange, WARM amber, COLD neutral. */
export const TEMPERATURE_BADGE: Record<PoolTemperature, string> = {
  HOT: 'border-transparent bg-orange-700 text-white dark:bg-orange-600',
  WARM: 'border-transparent bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200',
  COLD: 'border-transparent bg-muted text-muted-foreground',
}

/** The one-line "why it's hot" text. */
export function reasonsLine(row: Pick<PoolLeadItem, 'temperatureReasons'>): string | null {
  const reasons = (row.temperatureReasons ?? []).map((r) => r.trim()).filter(Boolean)
  return reasons.length > 0 ? reasons.join(' · ') : null
}

// ─── Waiting timer / claim SLA ─────────────────────────────────────────────────────

/** Whole minutes from `iso` to `now`; null when the date is missing or unreadable. */
export function minutesSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.floor((now - t) / 60_000))
}

/** "just now", "4 min", "1 h 12 min", "3 h", "2 days". */
export function formatWaiting(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const m = Math.max(0, Math.floor(minutes))
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min`
  if (m < 24 * 60) {
    const h = Math.floor(m / 60)
    const rest = m % 60
    return rest === 0 || h >= 6 ? `${h} h` : `${h} h ${rest} min`
  }
  const d = Math.floor(m / (24 * 60))
  return `${d} ${d === 1 ? 'day' : 'days'}`
}

export type TimerTone = 'fresh' | 'nearing' | 'overdue'

/**
 * How close a HOT lead is to its claim SLA: fresh in the first half, nearing in the second,
 * overdue once past it — or whenever the server already says `claimOverdue` (its clock wins).
 */
export function timerTone(minutes: number | null, claimSlaMinutes: number, claimOverdue?: boolean | null): TimerTone {
  if (claimOverdue) return 'overdue'
  if (minutes == null) return 'fresh'
  const sla = claimSlaMinutes > 0 ? claimSlaMinutes : 15
  // Strictly past the SLA, matching the server's claimOverdue rule.
  if (minutes > sla) return 'overdue'
  if (minutes >= sla / 2) return 'nearing'
  return 'fresh'
}

export const TIMER_TONE_CLASS: Record<TimerTone, string> = {
  fresh: 'text-emerald-700 dark:text-emerald-400',
  nearing: 'text-amber-700 dark:text-amber-400',
  overdue: 'text-red-700 dark:text-red-400',
}

/** "Updated 12 s ago" / "Updated 3 min ago" for the live indicator. */
export function formatUpdatedAgo(updatedAt: number | null, now: number): string {
  if (updatedAt == null) return 'connecting…'
  const s = Math.max(0, Math.floor((now - updatedAt) / 1000))
  if (s < 5) return 'updated just now'
  if (s < 60) return `updated ${s} s ago`
  return `updated ${Math.floor(s / 60)} min ago`
}

/** When the lead started waiting: `waitingSince`, else the latest enquiry, else creation. */
export function waitingStart(row: Pick<PoolLeadItem, 'waitingSince' | 'lastInboundAt' | 'createdAt'>): number {
  const t = Date.parse(row.waitingSince ?? row.lastInboundAt ?? row.createdAt ?? '')
  return Number.isFinite(t) ? t : 0
}

/**
 * HOT cards in claim-next's order: overdue first, longest-waiting first; then the rest newest
 * first. Stable for ties, so a refresh never shuffles equal rows.
 */
export function sortHotForClaim<T extends Pick<PoolLeadItem, 'claimOverdue' | 'waitingSince' | 'lastInboundAt' | 'createdAt'>>(
  rows: readonly T[],
): T[] {
  return rows
    .map((row, i) => ({ row, i, overdue: !!row.claimOverdue, t: waitingStart(row) }))
    .sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1
      const byTime = a.overdue ? a.t - b.t : b.t - a.t
      return byTime || a.i - b.i
    })
    .map((x) => x.row)
}

/** Every hot lead on screen is already past the claim SLA. */
export function allHotOverdue(rows: readonly Pick<PoolLeadItem, 'claimOverdue'>[]): boolean {
  return rows.length > 0 && rows.every((r) => !!r.claimOverdue)
}

/** The "claim before…" helper copy: an instruction to hurry when every hot lead is already late. */
export function hotHelperCopy(allOverdue: boolean, sla: number): string {
  return allOverdue ? `All hot leads are past the ${sla}-min mark — claim now` : `Claim before the ${sla}-min mark`
}

export const AT_CAP_REASON = "You're at your cap — work your untouched leads first"

/** L4: the rep may not claim. Managers are exempt; unknown counts never block. */
export function isAtHoldingCap(
  pulse: Pick<PoolPulse, 'myUntouchedClaims' | 'isManager'> | null | undefined,
  policy: Pick<DispatchPolicyApi, 'holdingCap' | 'holdingCapEnabled'>,
): boolean {
  if (!pulse || pulse.isManager || !policy.holdingCapEnabled) return false
  const held = pulse.myUntouchedClaims
  return typeof held === 'number' && held >= policy.holdingCap
}

/** Pipeline, scoped to me (its default), on the "Never contacted" focus view. */
export const UNTOUCHED_PIPELINE_LINK = '/sales/leads?focus=never'

// ─── Polling ───────────────────────────────────────────────────────────────────────

/** Whether a poll tick should fetch: never while hidden, and not again within the interval. */
export function shouldPoll(lastRunAt: number | null, now: number, intervalMs: number, hidden: boolean): boolean {
  if (hidden) return false
  if (lastRunAt == null) return true
  // A little slack so a timer firing a few ms early still counts as due.
  return now - lastRunAt >= intervalMs - 250
}

/**
 * The rows that genuinely ARRIVED since the previous poll — "N new leads". A row counts only when
 * its own clock (`lastInboundAt`, else `createdAt`) is after the previous poll time, so a lead that
 * merely moved into view (someone else's claim shifting the list, a page boundary) never toasts.
 * `since` null = first load: nothing is new.
 */
export function arrivedSince(
  rows: readonly Pick<PoolLeadItem, 'leadId' | 'lastInboundAt' | 'createdAt'>[],
  since: number | null,
): string[] {
  if (since == null) return []
  return rows
    .filter((r) => {
      const t = Date.parse(r.lastInboundAt ?? r.createdAt ?? '')
      return Number.isFinite(t) && t > since
    })
    .map((r) => r.leadId)
}

// ─── Brief chips (route / cargo / when) ────────────────────────────────────────────

export interface BriefChips {
  route?: string
  cargo?: string
  when?: string
}

function labelled(lines: string[], label: string): string | undefined {
  const line = lines.find((l) => l.startsWith(`${label}:`))
  const v = line?.slice(label.length + 1).trim()
  return v ? v : undefined
}

/**
 * The card's chips, read only from what the customer told us — never invented:
 *   route  ← `briefHeadline` (server), else the latest brief's `Service:` line
 *   cargo  ← the `Cargo:` line's cargo and `Weight:` parts
 *   when   ← the `Cargo:` line's `Urgency:` part, as "Urgency: High"
 * Email briefs have no labelled lines, so they usually give a route only via `briefHeadline`.
 */
export function briefChips(row: Pick<PoolLeadItem, 'briefHeadline' | 'notes'>): BriefChips {
  const chips: BriefChips = {}
  const latest = splitNotes(row.notes).briefs[0]
  const lines = latest
    ? latest.body
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
    : []
  const headline = row.briefHeadline?.trim()
  const route = headline || labelled(lines, 'Service')
  if (route) chips.route = route

  const cargoLine = labelled(lines, 'Cargo')
  if (cargoLine) {
    const parts = cargoLine.split('·').map((p) => p.trim()).filter(Boolean)
    const cargoParts: string[] = []
    for (const p of parts) {
      if (/^weight:/i.test(p)) {
        const w = p.replace(/^weight:\s*/i, '').trim()
        if (w) cargoParts.push(w)
      } else if (/^urgency:/i.test(p)) {
        const u = p.replace(/^urgency:\s*/i, '').trim()
        if (u) chips.when = `Urgency: ${u}`
      } else {
        cargoParts.push(p)
      }
    }
    if (cargoParts.length > 0) chips.cargo = cargoParts.join(' · ')
  }
  return chips
}

/** The "Read the request" link's wording, from the latest brief's channel. */
export function readRequestLabel(notes: string | null | undefined): string {
  const heading = splitNotes(notes).briefs[0]?.heading ?? ''
  if (/website/i.test(heading)) return 'Read the chat and form'
  if (/email/i.test(heading)) return 'Read the email'
  return 'Read the request'
}

// ─── Claim-as-type ─────────────────────────────────────────────────────────────────

export const TYPE_LABEL: Record<SettableCustomerType, string> = {
  DIRECT_CUSTOMER: 'Direct customer',
  FREIGHT_FORWARDER: 'Freight forwarder',
}

export function settableType(t: string | null | undefined): SettableCustomerType | undefined {
  return t === 'DIRECT_CUSTOMER' || t === 'FREIGHT_FORWARDER' ? t : undefined
}

/** Every success toast on the pool lasts 5 s; refusals keep Sonner's longer default. */
export const SUCCESS_TOAST_MS = 5_000

/** "Acme is yours" — and, for a claim-as-type, which type was applied (B5). */
export function claimSuccessTitle(name: string, customerType?: SettableCustomerType | null): string {
  return customerType ? `${name} is yours, claimed as ${TYPE_LABEL[customerType]}` : `${name} is yours`
}

export interface ClaimChoice {
  type: SettableCustomerType
  label: string
  /** The suggested type — rendered as the primary button, labelled "suggested". */
  suggested: boolean
}

/**
 * The split pair for an untyped lead. The suggested type goes LAST so it sits at the end of the
 * row as the primary action (the design's reading order); with no suggestion both are equal and
 * Direct customer — the more common answer — comes last.
 */
export function claimChoices(suggested: string | null | undefined): ClaimChoice[] {
  const s = settableType(suggested)
  const order: SettableCustomerType[] =
    s === 'FREIGHT_FORWARDER' ? ['DIRECT_CUSTOMER', 'FREIGHT_FORWARDER'] : ['FREIGHT_FORWARDER', 'DIRECT_CUSTOMER']
  return order.map((type) => ({ type, label: `Claim as ${TYPE_LABEL[type]}`, suggested: type === s }))
}

/** Untyped = no settable type on the row (PROVISIONAL, or the type missing). */
export function isUntyped(row: Pick<PoolLeadItem, 'customerType' | 'poolStatus'>): boolean {
  return row.poolStatus === 'PROVISIONAL' || !settableType(row.customerType)
}

// ─── Team dispatch ─────────────────────────────────────────────────────────────────

export const LOAD_LABEL: Record<RepLoad, string> = { FREE: 'Free', ROOM: 'Has room', FULL: 'Full' }

export const LOAD_TONE: Record<RepLoad, { bar: string; text: string }> = {
  FREE: { bar: 'bg-emerald-600 dark:bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
  ROOM: { bar: 'bg-emerald-600 dark:bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
  FULL: { bar: 'bg-orange-700 dark:bg-orange-500', text: 'text-orange-800 dark:text-orange-400' },
}

/** The load bar's fill: untouched claims against the cap (the cap counts untouched, L4). */
export function loadPercent(untouched: number, cap: number, load: RepLoad): number {
  if (load === 'FULL') return 100
  if (!(cap > 0)) return load === 'FREE' ? 10 : 50
  return Math.max(4, Math.min(100, Math.round((untouched / cap) * 100)))
}

export function normalizeLoad(raw: string | null | undefined): RepLoad {
  const v = (raw ?? '').toUpperCase()
  return v === 'FULL' || v === 'ROOM' ? v : 'FREE'
}

/** A rep's display name — the wire allows null. */
export function repName(rep: { name?: string | null; userId: string }): string {
  return rep.name?.trim() || rep.userId
}

/** The suggested owner's user id, from either wire shape (object or bare id). */
export function suggestedOwnerId(row: Pick<DispatchWaitingItem, 'suggestedOwner'> & { suggestedOwnerUserId?: string | null }): string | null {
  const s = row.suggestedOwner
  if (typeof s === 'string') return s || null
  if (s && typeof s === 'object' && s.userId) return s.userId
  return row.suggestedOwnerUserId ?? null
}

/** The waiting card's tag: OVERDUE beats the temperature. */
export function waitingTag(row: Pick<PoolLeadItem, 'temperature' | 'claimOverdue'>): 'OVERDUE' | PoolTemperature {
  return row.claimOverdue ? 'OVERDUE' : temperatureOf(row)
}

// ─── Response normalisers (the contract names the data, not every field spelling) ──

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isFinite(n) ? n : null
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

function arrayIn(data: unknown, keys: string[]): unknown[] {
  if (Array.isArray(data)) return data
  const r = rec(data)
  for (const k of keys) if (Array.isArray(r[k])) return r[k] as unknown[]
  return []
}

/** GET /pool/leaderboard → rows sorted by claims (desc), then speed (asc). */
export function normalizeLeaderboard(data: unknown): LeaderboardEntry[] {
  return arrayIn(data, ['entries', 'reps', 'leaderboard', 'rows'])
    .map((raw) => {
      const r = rec(raw)
      return {
        userId: str(r.userId) ?? '',
        name: str(r.name) ?? str(r.userName) ?? str(r.userId) ?? 'Unknown',
        claimsToday: num(r.claimsToday) ?? num(r.claimedToday) ?? num(r.claims) ?? 0,
        medianClaimMinutes: num(r.medianClaimMinutes) ?? num(r.medianClaimMinutesToday) ?? num(r.medianMinutes),
      }
    })
    .sort(
      (a, b) =>
        b.claimsToday - a.claimsToday ||
        (a.medianClaimMinutes ?? Number.MAX_SAFE_INTEGER) - (b.medianClaimMinutes ?? Number.MAX_SAFE_INTEGER) ||
        a.name.localeCompare(b.name),
    )
}

/** POST /pool/auto-distribute → assigned + skipped, whatever the server called them. */
export function normalizeAutoDistribute(data: unknown): AutoDistributeResult {
  const assigned = arrayIn(data, ['assigned', 'assignments']).map((raw) => {
    const r = rec(raw)
    return {
      leadId: str(r.leadId) ?? '',
      leadName: str(r.leadName) ?? str(r.name),
      userId: str(r.userId) ?? str(r.assignedToUserId) ?? '',
      userName: str(r.userName) ?? str(r.assignedToUserName),
    }
  })
  const skipped = (Array.isArray(rec(data).skipped) ? (rec(data).skipped as unknown[]) : []).map((raw) => {
    const r = rec(raw)
    return {
      leadId: str(r.leadId) ?? '',
      leadName: str(r.leadName) ?? str(r.name),
      // The message is written for people; the reason is a code. Prefer the message.
      reason: str(r.message) ?? str(r.reason) ?? 'Skipped',
    }
  })
  return { assigned, skipped }
}

/** The result toast's lines. */
export function autoDistributeSummary(result: AutoDistributeResult): { title: string; description?: string } {
  const a = result.assigned.length
  const s = result.skipped.length
  const title =
    a === 0 && s === 0
      ? 'Nothing to distribute'
      : `Assigned ${a} ${a === 1 ? 'lead' : 'leads'}${s > 0 ? ` · skipped ${s}` : ''}`
  if (s === 0) {
    const who = [...new Set(result.assigned.map((x) => x.userName).filter(Boolean))]
    return { title, description: who.length > 0 ? `To ${who.join(', ')}.` : undefined }
  }
  const lines = result.skipped
    .slice(0, 3)
    .map((x) => `${x.leadName ?? x.leadId}: ${x.reason}`)
  if (s > 3) lines.push(`…and ${s - 3} more`)
  return { title, description: lines.join('\n') }
}

/**
 * POST /pool/claim-next → `{lead, appliedType, appliedSuggested}`. An older server returned the
 * row itself; that still reads, with nothing applied.
 */
export function normalizeClaimNext(data: unknown): ClaimNextResult | null {
  const r = rec(data)
  const leadRaw = r.lead && typeof r.lead === 'object' ? r.lead : str(r.leadId) ? data : null
  if (!leadRaw) return null
  return {
    lead: leadRaw as PoolLeadItem,
    appliedType: settableType(str(r.appliedType)) ?? null,
    appliedSuggested: r.appliedSuggested === true,
  }
}

/**
 * Whether a just-claimed lead is HOT — the server's temperature, else an enquiry within `hotHours`.
 * Only a HOT claim starts the first-call clock (L3).
 */
export function isHotLead(
  lead: Pick<PoolLeadItem, 'temperature' | 'lastInboundAt'>,
  hotHours: number,
  now: number,
): boolean {
  if (lead.temperature) return temperatureOf(lead) === 'HOT'
  const t = Date.parse(lead.lastInboundAt ?? '')
  return Number.isFinite(t) && now - t <= hotHours * 3_600_000
}

/**
 * The claim-next success toast. A suggested type applied on the rep's behalf is said out loud; the
 * first-call deadline is mentioned only for a HOT lead (the only kind with one).
 */
export function claimNextToast(
  result: ClaimNextResult,
  opts: { firstCallSlaMinutes: number; hotHours: number; now: number },
): { title: string; description?: string; offerProfile: boolean } {
  const hot = isHotLead(result.lead, opts.hotHours, opts.now)
  const due = hot ? `First call due within ${formatWaiting(opts.firstCallSlaMinutes)}.` : undefined
  if (result.appliedSuggested && result.appliedType) {
    return {
      title: `Claimed as ${TYPE_LABEL[result.appliedType]} (suggested) — change it in Profile`,
      description: [`${result.lead.name} is yours.`, due].filter(Boolean).join(' '),
      offerProfile: true,
    }
  }
  return { title: `${result.lead.name} is yours`, description: due, offerProfile: false }
}

/** The header button: "Claim next hot lead" only while there is a hot lead to claim. */
export function claimNextLabel(hotUnclaimed: number | null | undefined): string {
  return hotUnclaimed === 0 ? 'Claim next lead' : 'Claim next hot lead'
}
